import { Injectable } from "@angular/core";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
// @ts-ignore
import { NgbModalOptions } from "@ng-bootstrap/ng-bootstrap/modal/modal-config";
import { MobileBackService } from "@core/services/mobile-back.service";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription } from "rxjs";

@Injectable({
  providedIn: 'root'
})

export class ModalCustomService {
  private readonly _historyCleanup = new WeakMap<NgbModalRef, Promise<void>>();

  constructor(
    private _modalService: NgbModal,
    private _mobileBackService: MobileBackService,
    private _router: Router,
  ) {
  }

  /**
   * Tự động đăng ký Modal với MobileBackService.
   * Giúp đóng modal khi vuốt Back hoặc bấm Back trên điện thoại/trình duyệt.
   */
  private _handleMobileBack(modalRef: NgbModalRef) {
    const backId = this._mobileBackService.push(() => {
      modalRef.dismiss('hardware_back');
    });

    const cleanupPromise = modalRef.result.then(
      () => this._mobileBackService.popAndWait(backId),
      () => this._mobileBackService.popAndWait(backId),
    );

    this._historyCleanup.set(modalRef, cleanupPromise);
  }

  /**
   * Chờ modal đóng và hoàn tất dọn dẹp history trước khi trả về kết quả, tránh xung đột khi mở modal tiếp theo.
   */
  async waitForResult<T>(modalRef: NgbModalRef): Promise<T> {
    try {
      const result = await modalRef.result as T;
      await (this._historyCleanup.get(modalRef) ?? Promise.resolve());
      return result;
    } catch (reason) {
      await (this._historyCleanup.get(modalRef) ?? Promise.resolve());
      throw reason;
    }
  }

  /**
   * Mở modal với cấu hình mặc định cho phép đóng khi click vùng overlay hoặc Esc.
   * @param content Nội dung của modal
   * @param options Các tùy chọn của modal (nếu có)
   * @param skipMobileBack Nếu true, sẽ KHÔNG chèn lịch sử ảo (Dành cho các modal quản lý bằng URL params)
   */
  openDefault(content: any, options?: NgbModalOptions, skipMobileBack: boolean = false): NgbModalRef {
    const modalOptions: NgbModalOptions = {
      backdrop: true,
      keyboard: true,
      ...(options ?? {}),
    };
    const modalRef = this._modalService.open(content, modalOptions);
    if (!skipMobileBack) {
      this._handleMobileBack(modalRef);
    }
    return modalRef;
  }

  /**
   * Mở modal với các tùy chọn hiển thị được truyền vào từ bên ngoài.
   * @param content Nội dung của modal
   * @param options Các tùy chọn của modal (nếu có)
   * @param skipMobileBack Nếu true, sẽ KHÔNG chèn lịch sử ảo (Dành cho các modal quản lý bằng URL params)
   */
  openIntro(content: any, options?: NgbModalOptions, skipMobileBack: boolean = false): NgbModalRef {
    const modalOptions: NgbModalOptions = {
      backdrop: true,
      keyboard: true,
      ...(options ?? {}),
    };
    const modalRef = this._modalService.open(content, modalOptions);
    if (!skipMobileBack) {
      this._handleMobileBack(modalRef);
    }
    return modalRef;
  }

  /**
   * Đóng modal.
   */
  dismissAll() {
    return this._modalService.dismissAll();
  }

  hasOpenModals(): boolean {
    return this._modalService.hasOpenModals();
  }

  /**
   * Mở Modal kết hợp quản lý URL Params (Pattern 2).
   * Dùng hàm này thay cho openIntro() thông thường nếu muốn Modal có đường link riêng (VD: ?modal=demo).
   * Ưu điểm:
   * - Hỗ trợ Deep link (nhấn F5 vẫn giữ modal).
   * - Tự động bắt nút Back để đóng Modal.
   * - Tự dọn dẹp lịch sử, không sinh ra lịch sử rác khi đóng (giải quyết lỗi lặp phím Next/Back).
   *
   * @param route ActivatedRoute của component hiện tại
   * @param paramKey Tên của query param trên URL (VD: 'modal')
   * @param paramValue Giá trị của param để kích hoạt modal này (VD: 'demo')
   * @param content Component của modal (VD: DemoModalContentComponent)
   * @param options Cấu hình NgbModalOptions
   * @returns NgbModalRef để truyền data (modalRef.componentInstance.data = ...)
   */
  openUrlModal(
    route: ActivatedRoute,
    paramKey: string,
    paramValue: string,
    content: any,
    options?: NgbModalOptions
  ): NgbModalRef {

    // 1. Kiểm tra xem param đã có sẵn trên URL chưa (Trường hợp user F5 hoặc click từ link chia sẻ)
    const isAlreadyInUrl = route.snapshot.queryParams[paramKey] === paramValue;

    if (!isAlreadyInUrl) {
      // 2. Nếu mở bằng nút bấm: Tự động push param lên URL
      this._router.navigate([], { queryParams: { [paramKey]: paramValue }, queryParamsHandling: 'merge' });
    }

    // 3. Mở modal bằng hàm openIntro sẵn có, truyền true để BỎ QUA việc chèn lịch sử ảo
    // (Vì URL Param đã đóng vai trò là lịch sử rồi)
    const activeRef = this.openIntro(content, options, true);

    // Biến cờ theo dõi trạng thái URL để chống lỗi async của Router
    let hasUrlBeenSet = isAlreadyInUrl;

    // 4. Lắng nghe URL thay đổi để tự đóng Modal khi user nhấn Back
    const sub: Subscription = route.queryParams.subscribe(params => {
      if (params[paramKey] === paramValue) {
        hasUrlBeenSet = true;
      } else if (hasUrlBeenSet) {
        // NẾU MẤT PARAM (Do user vuốt Back): Tự tắt Modal
        activeRef.dismiss('route_changed');
      }
    });

    // 5. Lắng nghe sự kiện Modal đóng (do bấm X, click nền, hoặc dismiss ở trên)
    activeRef.result.finally(() => {
      // Dọn dẹp listener để không rò rỉ bộ nhớ
      sub.unsubscribe();

      // NẾU PARAM VẪN CÒN TRÊN URL (Nghĩa là modal bị đóng bằng nút X, chứ ko phải do vuốt Back)
      // Ta cần phải xoá param đó đi.
      if (route.snapshot.queryParams[paramKey] === paramValue) {
        if (!isAlreadyInUrl) {
          // Trường hợp trước đó mở bằng nút bấm: Ta gọi history.back() để lùi lại đúng 1 bước
          // Giúp URL sạch sẽ, KHÔNG ĐẺ THÊM lịch sử tiến (Tránh lỗi nhấn Back lại bị mở modal ra)
          this._mobileBackService.backWithoutHandling();
        } else {
          // Trường hợp F5 (deep link): Không có lịch sử cũ của trang web để back.
          // Buộc phải dùng replaceUrl để xoá param trực tiếp.
          this._router.navigate([], { queryParams: { [paramKey]: null }, queryParamsHandling: 'merge', replaceUrl: true });
        }
      }
    });

    return activeRef;
  }
}
